import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import NProgress from '@kittl/nprogress';
import Cookies from 'js-cookie';

import { FormWrapper, IconWrapper } from './styles';
import { errorTheme } from './theme';

import { Button, primaryTheme } from '@repo/ui/components/Buttons';
import TextInput from '../../TextInput/TextInput';
import { P4 } from '@repo/ui/components/Typography';
import { validators } from '@repo/ui/components/Inputs';
import { EventCatcherScope } from '@repo/event-processor';

import { userStoreSelector, useUserStore } from '../../../stores/userStore';
import { formatResponseError } from './utils';

import { FlexRow } from '@editor/components/utilities/layouts';
import analytics from '@repo/analytics';
import {
  CREATE_ACCOUNT,
  LOGIN,
  LOGIN_LOCAL_ERROR,
  LOGIN_SERVER_ERROR,
  SIGNUP_LOCAL_ERROR,
  SIGNUP_MOBILE,
  SIGNUP_SERVER_ERROR,
} from '../../../global/analytics/events';
import { isDesktopOperatingSystem } from '@repo/detection';
import { getDeviceScreenSizeType } from '@repo/ui/queries';
import { IMPACT_CLICK_ID } from '../../../global/constants';
import { themeStyle } from '@repo/ui/theming';
import { readUtmData } from '../../../utils/utm';
import InfoThickIcon from '@repo/ui/components/Icon/InfoThickIcon';
import { buildSignupUrl } from '@repo/utils/url';
import { Spacer } from '@repo/ui/components/Spacer';
import { getAdClickId } from '../../../utils/adClickId';

/**
 * Login/ Signup form within the AuthComponent
 */
const Form = ({
  email,
  error: errorFromProps,
  isSignup,
  inviteCode,
  teamInviteCode,
  referralCode,
  promoCode,
  onSubmit,
  callToAction,
  disableOnError,
  pow,
}) => {
  const [wrongAttempt, setWrongAttempt] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const { challenge, fetchChallenge, isChallengeReady } = pow;
  const [otpExchangeToken, setOtpExchangeToken] = useState(null);

  const [form, setForm] = useState({
    name: '',
    email: email || '',
    password: '',
    // Only used for 2FA
    otpCode: null,
  });
  const [error, setError] = useState({
    name: null,
    email: null,
    password: null,
    server: null,
  });

  useEffect(() => {
    setError((prev) => ({ ...prev, server: errorFromProps }));
  }, [errorFromProps]);

  useEffect(() => {
    if (error.server && !wrongAttempt) {
      setWrongAttempt(true);
    }
  }, [error.server, wrongAttempt]);

  const validateValue = (prop, value) => {
    switch (prop) {
      case 'name':
        return validators.name(value);
      case 'email':
        return validators.email(value);
      case 'password':
        return validators.password(value);
      default:
        return null;
    }
  };

  const resetError = () => {
    setError({
      name: null,
      email: null,
      password: null,
      server: null,
    });
  };

  const handleValidation = (prop, value) => {
    const error = validateValue(prop, value);
    setError((prev) => ({ ...prev, [prop]: error }));
  };

  const handleValidationReset = (prop, value) => {
    const error = validateValue(prop, value);
    if (!error) {
      setError((prev) => ({ ...prev, [prop]: null }));
    }
  };

  const handleCleaning = (prop, value) => {
    if (prop !== 'password') {
      return value.trim();
    }
    return value;
  };

  const handleSubmitValidation = () => {
    const errors = { name: null, email: null, password: null, server: null };
    if (isSignup) {
      errors.name = validateValue('name', form.name);
    }
    errors.email = validateValue('email', form.email);
    errors.password = validateValue('password', form.password);
    const hasErrors = Object.entries(errors).some(([_, err]) => err !== null);
    if (hasErrors && disableOnError) {
      setWrongAttempt(true);
    }
    return { errors, hasErrors };
  };

  const updateForm = (prop, value) => {
    const cleanValue = handleCleaning(prop, value);
    disableOnError && resetError();
    handleValidationReset(prop, cleanValue);
    setForm((prev) => ({ ...prev, [prop]: cleanValue }));
    setWrongAttempt(false);
  };

  const handleSubmit = () => {
    if (disableOnError && wrongAttempt) {
      return;
    }

    const { errors, hasErrors } = handleSubmitValidation();
    setError({ ...errors });
    Object.keys(errors).forEach((key) => {
      if (errors[key]) {
        analytics.track(isSignup ? SIGNUP_LOCAL_ERROR : LOGIN_LOCAL_ERROR, {
          label: `field: ${key}; error: ${errors[key]}`,
        });
      }
    });
    if (hasErrors || isLoading) {
      return;
    }

    analytics.track(isSignup ? CREATE_ACCOUNT : LOGIN, {
      label: `email: ${form.email}; name: ${form.name}`,
    });

    setLoading(true);
    NProgress.start();
  };

  const createUser = useUserStore(userStoreSelector.createUser);
  const loginUser = useUserStore(userStoreSelector.loginUser);

  const impactClickId = Cookies.get(IMPACT_CLICK_ID);
  const adClickId = getAdClickId();

  const submitForm = useCallback(() => {
    const action = isSignup ? createUser : loginUser;

    const args = {
      name: form.name,
      email: form.email,
      password: form.password,
      invite: inviteCode,
      teamInviteId: teamInviteCode,
      referral: referralCode,
      promoCode: promoCode,
      impactClickId,
      adClickId,
      otpCode: form.otpCode,
      otpExchangeToken,
    };

    const utm = isSignup && readUtmData();

    if (utm) {
      args.utm = utm;
    }

    const signupUrl = isSignup && buildSignupUrl();

    if (signupUrl) {
      args.signupUrl = signupUrl;
    }

    const deviceScreenSizeType = isSignup && getDeviceScreenSizeType();

    if (deviceScreenSizeType) {
      args.deviceScreenSizeType = deviceScreenSizeType;
    }

    if (challenge) {
      args.powChallenge = challenge;
    }

    return action(args);
  }, [
    isSignup,
    createUser,
    loginUser,
    form.name,
    form.email,
    form.password,
    form.otpCode,
    challenge,
    inviteCode,
    teamInviteCode,
    referralCode,
    promoCode,
    impactClickId,
    adClickId,
    otpExchangeToken,
  ]);

  // The actual submission is moved here, because `Form` can be unmounted when the request is completed,
  // on which we cannot perform set state by then.
  useEffect(() => {
    let isRendered = true;
    // prevent any login submit before the challenge is solved, unless the pow is disabled, and ensure it will be used just once
    const submit = async () => {
      let response;
      try {
        response = await submitForm();
        if (isSignup && !isDesktopOperatingSystem()) {
          analytics.track(SIGNUP_MOBILE);
        }

        if (!isSignup && response.exchangeToken) {
          // User has been given temporary access to complete 2FA

          // Set the exchange token to be used for 2FA
          setOtpExchangeToken(response.exchangeToken);

          // Fetch another challenge to prevent the same challenge being used again
          fetchChallenge();

          return;
        }

        if (response.error) {
          formatResponseError(response, form, (prop, error) => {
            if (isRendered) {
              setError((prev) => ({ ...prev, [prop]: error }));
            }

            analytics.track(
              isSignup ? SIGNUP_SERVER_ERROR : LOGIN_SERVER_ERROR,
              {
                label: `field: ${prop}; error: ${error}`,
              }
            );
          });
          setOtpExchangeToken(null);
        } else {
          onSubmit && onSubmit();
        }
      } finally {
        NProgress.done();
        if (isRendered) {
          setLoading(false);
          // Fetch another challenge if an error occured to prevent submit delay
          if (response.error) {
            fetchChallenge();
          }
        }
      }
    };
    if (isLoading && isChallengeReady) {
      submit();
    }

    return () => {
      isRendered = false;
    };
  }, [
    isLoading,
    isSignup,
    onSubmit,
    form,
    submitForm,
    isChallengeReady,
    fetchChallenge,
    setOtpExchangeToken,
  ]);

  const formName = isSignup ? 'signupForm' : 'loginForm';
  return (
    <EventCatcherScope module={formName}>
      <FormWrapper name={formName}>
        {isSignup && (
          <>
            <TextInput
              error={error.name}
              name="name"
              placeholder="Name"
              onBlur={() => handleValidation('name', form.name)}
              onChanging={(name) => updateForm('name', name)}
              onEnter={handleSubmit}
            />
            <Spacer height={error.name ? '8px' : '12px'} />
          </>
        )}
        <TextInput
          autoComplete="username"
          error={error.email}
          name="email"
          placeholder="E-Mail"
          value={form.email}
          onBlur={() => handleValidation('email', form.email)}
          onChanging={(email) => updateForm('email', email)}
          onEnter={handleSubmit}
        />
        <Spacer height={error.email ? '8px' : '12px'} />
        <TextInput
          isPasswordInput
          autoComplete={isSignup ? 'new-password' : 'current-password'}
          error={error.password}
          name="password"
          placeholder="Password"
          onBlur={() => handleValidation('password', form.password)}
          onChanging={(password) => updateForm('password', password)}
          onEnter={handleSubmit}
        />
        <Spacer height={error.password ? '8px' : '12px'} />
        {otpExchangeToken && (
          <>
            <TextInput
              name="otpCode"
              onChanging={(otpCode) => updateForm('otpCode', otpCode)}
              onEnter={handleSubmit}
            />
            <Spacer height="12px" />
            <FlexRow alignItems="center">
              <IconWrapper>
                <InfoThickIcon height="18px" />
              </IconWrapper>
              <P4>Please enter the code sent to your email.</P4>
            </FlexRow>
            <Spacer height="12px" />
          </>
        )}
        {error.server && (
          <>
            <FlexRow alignItems="center">
              <IconWrapper>
                <InfoThickIcon height="18px" theme={errorTheme} />
              </IconWrapper>
              <P4 color={themeStyle.varAlertRed}>{error.server}</P4>
            </FlexRow>
            <Spacer height="12px" />
          </>
        )}
        <Button
          dataTestId={isSignup ? 'signup' : 'login'}
          disabled={isLoading || (disableOnError ? wrongAttempt : false)}
          height="40px"
          label={
            callToAction
              ? callToAction
              : isSignup
                ? 'Create Free Account'
                : 'Sign In'
          }
          name={isSignup ? 'signupButton' : 'loginButton'}
          theme={primaryTheme}
          type="button"
          width="100%"
          onClick={handleSubmit}
        />
      </FormWrapper>
    </EventCatcherScope>
  );
};

Form.propTypes = {
  /**
   * Handler for when the form is submitted and the response is errorless
   */
  onSubmit: PropTypes.func,
  /**
   * Whether the form will be used to sign up
   */
  isSignup: PropTypes.bool,
  /**
   * pre-fill the form with an email address
   */
  email: PropTypes.string,
  /**
   * inviteCode for the signup
   */
  inviteCode: PropTypes.string,
  /**
   * team invite code for the signup or login
   */
  teamInviteCode: PropTypes.string,
  /**
   * referralCode for the signup
   */
  referralCode: PropTypes.string,
  /**
   * promoCode for the signup
   */
  promoCode: PropTypes.string,
  /**
   * nonce and solution of the proof-of-work and the handlers to refresh the challenge
   */
  pow: PropTypes.shape({
    challenge: PropTypes.shape({
      isEnabled: PropTypes.bool,
      nonce: PropTypes.string,
      solution: PropTypes.number,
    }),
    fetchChallenge: PropTypes.func,
    isChallengeReady: PropTypes.bool,
  }),
  /**
   * error message that will be displayed as a server error
   */
  error: PropTypes.string,
  callToAction: PropTypes.string,
  disableOnError: PropTypes.bool,
};

export default Form;
